// SearchTypeahead — debounced, typeahead search across airports / navaids
// / fixes via /api/search. On select, the parent flies the map and opens
// a DetailPane.
//
// Tail-number detection: if the query matches a US N-number pattern
// (N followed by 1-5 alphanumerics, e.g. N629RA), the component calls
// onTailLookup instead of onSelect so the parent can open TailLookupSheet.

import { useEffect, useRef, useState } from 'react';
import { Icon } from '../shell/Icon';
import { searchAny, SearchHit } from '../lib/search';

/** Matches US N-numbers: N + 1-5 alphanumerics. */
const N_NUMBER_RE = /^N[0-9][0-9A-Z]{0,4}$/;

export function isNNumber(q: string): boolean {
  return N_NUMBER_RE.test(q.trim().toUpperCase());
}

interface Props {
  onSelect: (hit: SearchHit) => void;
  /** Called when the submitted query looks like a tail number. */
  onTailLookup?: (ident: string) => void;
}

const DEBOUNCE_MS = 150;
const LIMIT = 20;

export default function SearchTypeahead({ onSelect, onTailLookup }: Props) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounced search effect. Aborts in-flight requests AND the pending
  // debounce timeout on every dep change *and* on unmount, so a stale
  // response can't ever call setHits/setLoading after the component is
  // gone.
  useEffect(() => {
    abortRef.current?.abort();
    if (!q.trim()) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ac = new AbortController();
    abortRef.current = ac;
    const t = setTimeout(async () => {
      try {
        const results = await searchAny(q, { limit: LIMIT, signal: ac.signal });
        if (!ac.signal.aborted) {
          setHits(results);
          setLoading(false);
        }
      } catch (e: any) {
        if (e?.name !== 'AbortError') setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [q]);

  // Track the blur-close timeout so we can cancel it on unmount and on
  // every new blur event. Without this, the timeout can fire after the
  // component unmounts and call setOpen on a dead instance.
  const blurTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (blurTimerRef.current != null) window.clearTimeout(blurTimerRef.current);
  }, []);

  const choose = (h: SearchHit) => {
    onSelect(h);
    setQ('');
    setHits([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-lg px-3 h-10">
        <Icon name="search" className="h-4 w-4 text-cockpit-300" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            if (blurTimerRef.current != null) window.clearTimeout(blurTimerRef.current);
            blurTimerRef.current = window.setTimeout(() => setOpen(false), 180);
          }}
          placeholder="Search airports, navaids, fixes, tail numbers…"
          aria-label="Search airports, navaids, fixes, tail numbers"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onTailLookup && isNNumber(q)) {
              e.preventDefault();
              const ident = q.trim().toUpperCase();
              setQ('');
              setHits([]);
              setOpen(false);
              inputRef.current?.blur();
              onTailLookup(ident);
            }
          }}
          className="flex-1 bg-transparent outline-none text-sm placeholder-cockpit-400"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          inputMode="search"
        />
        {q && (
          <button
            onClick={() => { setQ(''); setHits([]); inputRef.current?.focus(); }}
            className="text-cockpit-400 hover:text-cockpit-200 text-sm"
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {open && (q || loading) && (
        <div className="absolute left-0 right-0 mt-1 max-h-[60vh] overflow-y-auto scroll-touch bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-lg shadow-rail">
          {/* Tail-number quick-action row when the query looks like an N-number */}
          {onTailLookup && isNNumber(q) && (
            <button
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => {
                const ident = q.trim().toUpperCase();
                setQ('');
                setHits([]);
                setOpen(false);
                inputRef.current?.blur();
                onTailLookup(ident);
              }}
              className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-cockpit-700 text-left border-b border-cockpit-700"
            >
              <span className="shrink-0 text-[9px] font-mono tracking-widest border rounded px-1.5 py-0.5 bg-sky-500/30 text-sky-200 border-sky-500/40">
                TAIL
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-cockpit-100 font-mono leading-tight">{q.trim().toUpperCase()}</div>
                <div className="text-[11px] text-cockpit-300 leading-tight">Look up flight history</div>
              </div>
            </button>
          )}
          {loading && hits.length === 0 ? (
            <div className="text-xs text-cockpit-300 p-3 text-center">Searching…</div>
          ) : hits.length === 0 && !isNNumber(q) ? (
            <div className="text-xs text-cockpit-300 p-3 text-center">No matches</div>
          ) : hits.length === 0 && isNNumber(q) ? null : (
            <ul>
              {hits.map((h, i) => (
                <li key={`${h.kind}-${h.ident}-${i}`}>
                  <button
                    // pointerdown covers mouse + touch + pencil on iPad,
                    // unlike the old mousedown-only handler which let
                    // the blur-close timeout race the tap on iOS.
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => choose(h)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-cockpit-700 text-left"
                  >
                    <KindBadge kind={h.kind} badge={h.badge} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-cockpit-100 font-mono leading-tight">{h.ident}</div>
                      <div className="text-[11px] text-cockpit-300 truncate leading-tight">
                        {h.name}{h.sub ? ` · ${h.sub}` : ''}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function KindBadge({ kind, badge }: { kind: SearchHit['kind']; badge?: string }) {
  const label = badge ?? (kind === 'airport' ? 'APT' : kind === 'navaid' ? 'NAV' : 'FIX');
  const color =
    kind === 'airport' ? 'bg-ember-500/30 text-ember-300 border-ember-500/40'
    : kind === 'navaid' ? 'bg-cyan-500/30 text-cyan-200 border-cyan-500/40'
    : 'bg-violet-500/30 text-violet-200 border-violet-500/40';
  return (
    <span className={`shrink-0 text-[9px] font-mono tracking-widest border rounded px-1.5 py-0.5 ${color}`}>
      {label}
    </span>
  );
}
